import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Button, Grid, Theme, Typography } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { IFlightType, UserInboxItemType } from "@vmosolutions/oc-interfaces";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AuditEventsCheck } from "src/components/Audit/AuditEventsCheck";
import { FileUpload } from "src/components/FileUpload/FileUpload";
import { FormikCheckboxField, FormikSelectField } from "src/components/FormikComponents/FormikWrappers";
import { IApplicationProps } from "src/redux/actions/App.Actions";
import { addAuditToWatch } from "src/redux/actions/Audit/Audit.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IStructuresState } from "src/redux/reducers/StructuresReducer";
import { uploadToUserInbox } from "src/utils/userInboxUpload";

interface IImportEventsWindowProps extends IApplicationProps {
  path: string[];
  id?: string;
}

interface IContentFileInfo {
  name: string;
  path: string;
  size: number;
  type: string;
  webkitRelativePath: string;
  lastModified: number;
  lastModifiedDate: Date;
}

interface IContentImport {
  currentAuditId: string; // Used to track current performed audit on content dropped in.

  fileInfo: IContentFileInfo;
  fileContents: ArrayBuffer;

  assignToAircraft: boolean;
  updatePastFlights: boolean;
  matchFlightNum: boolean;
  assignToFlightType: IFlightType;
}
type ContentImportForm = FormikProps<IContentImport>;

const ImportEventWstyles = (theme: Theme): any => ({
  containerDiv: {
    color: "white",
    display: "flex",
    margin: "4px",
    padding: "4px",
    flexDirection: "column",
    backgroundColor: theme.custom.timeline.contextListBackground,

    "& .content_import_form": {
      "& .selectfield": {
        width: "250px",
        "& .input_control label": { width: "auto" },
      },
      "& .checkboxfield": { width: "170px" },
      "& .import_btn": {
        border: "1px solid rgba(255, 255, 255, 0.23)",
      },

      "& .form_footer, & .audit_table": {
        marginTop: "10px",
        display: "flex",
        justifyContent: "flex-start",
        "& button": { margin: "5px 5px" },
        "& .btn": {
          flexWrap: "wrap",
          display: "flex",
          padding: "2px 8px",
          margin: "0px 4px",
        },
      },
    },
  },
});

export const ImportEventsWindow = (props: IImportEventsWindowProps): JSX.Element => {
  const { path, id } = props;
  const dispatch = useDispatch();
  const fileUploadRef = useRef<any>(null);
  const [loadingAudit, setLoadingAudit] = useState<boolean>(false);
  const structures: IStructuresState = useSelector((state: IRootState) => state.structures);
  const { customerStructure } = structures;
  const { flightTypes } = customerStructure;

  const flightEventTypes = useMemo(() => {
    if (flightTypes) {
      return flightTypes
        .filter((type: IFlightType) => type.isFlight)
        .sort((a, b) => {
          return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
        });
    }
    return [];
  }, [flightTypes]);

  const defaultImportValues = useMemo(() => {
    return {
      currentAuditId: undefined,
      assignToAircraft: true,
      updatePastFlights: false,
      matchFlightNum: true,
      assignToFlightType: flightEventTypes?.length > 0 ? flightEventTypes[0] : null,
      fileContents: undefined,
      fileInfo: undefined,
    };
  }, [flightEventTypes]);

  const handleSubmit = useCallback(
    async (values: IContentImport, helpers: FormikHelpers<IContentImport>): Promise<void> => {
      const { setSubmitting, setStatus, setFieldValue, setFieldTouched } = helpers;
      const { assignToAircraft, updatePastFlights, matchFlightNum, assignToFlightType, fileContents, fileInfo } =
        values;
      setLoadingAudit(true);

      const auditId = await uploadToUserInbox(
        UserInboxItemType.FlightImport,
        fileInfo?.name,
        fileInfo?.type,
        fileInfo?.lastModified,
        fileContents,
        ["oc-assign-to-ac", assignToAircraft?.toString()],
        ["oc-match-flight-number", matchFlightNum.toString()],
        ["oc-flt-type", assignToFlightType?._id],
        ["oc-update-past-flights", updatePastFlights.toString()]
      );

      // If new auditId is successfully created, reset form.
      if (auditId && typeof auditId === "string") {
        dispatch(addAuditToWatch(auditId));
        setFieldValue("currentAuditId", auditId);
        setFieldTouched("currentAuditId", true);
        setStatus({ success: true });
      } else {
        setStatus({ success: false });
      }
    },
    [dispatch]
  );

  return (
    <Grid sx={(theme) => ImportEventWstyles(theme).containerDiv} container spacing={0}>
      <Grid xs={12} item>
        <Formik initialValues={defaultImportValues} enableReinitialize={true} onSubmit={handleSubmit}>
          {(formProps: ContentImportForm) => {
            const { values, dirty, isSubmitting, handleReset, setFieldValue, setFieldTouched } = formProps;
            const auditDataLoaded = !!values?.currentAuditId;
            const displayImport = !values?.currentAuditId && !isSubmitting;
            const isReadyToAudit = values?.fileInfo && values.fileContents;
            const auditAccordionTitle = isReadyToAudit
              ? `Audit Events in file "${values?.fileInfo?.path}"`
              : "Audit Events";
            const auditSubTitle =
              loadingAudit && values?.fileInfo?.path
                ? `Loading file "${values?.fileInfo?.path}" into audit table (this may take a while if file contains many audit entries)`
                : "Compare what is loaded in OC to file contents";

            // Used when resetting the import tool or backing out of the audit table.
            const handleResetImport = (): void => {
              handleReset();
              if (fileUploadRef?.current?.hasOwnProperty("removeAllFiles")) {
                fileUploadRef.current.removeAllFiles();
              }
            };

            /**
             * Update the form with the current file info and contents.
             * @param _newFile -
             * @param _fileContents -
             */
            const gotFile = (_newFile: any, _fileContents: ArrayBuffer): void => {
              if (_newFile && _fileContents) {
                setFieldValue("fileInfo", _newFile);
                setFieldTouched("fileInfo", true);
                setFieldValue("fileContents", _fileContents);
                setFieldTouched("fileContents", true);
              }
            };

            const handleRemoveFile = (): void => {
              setFieldValue("fileInfo", null);
              setFieldTouched("fileInfo", true);
              setFieldValue("fileContents", null);
              setFieldTouched("fileContents", true);
            };

            return (
              <Form className={`content_import_form ${auditDataLoaded ? "table_visible" : ""}`}>
                <Accordion expanded={displayImport} className="import_accordion">
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ opacity: displayImport ? 1 : 0.5 }}>
                    <Typography variant="h6" className="title" style={{ marginBottom: 10 }}>
                      Flight Display - Event Import Tool
                    </Typography>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Grid container direction="column" className="file_import">
                      <FileUpload ref={fileUploadRef} onFile={gotFile} handleRemoveFile={handleRemoveFile} />
                      <Grid container direction="row" style={{ margin: "10px 0px" }}>
                        <FormikSelectField
                          className="field selectfield"
                          name="assignToFlightType"
                          label="Assign to Flight Type"
                          getOptionLabel={(option: IFlightType) => option?.description ?? ""}
                          getOptionValue={(option: IFlightType) => option._id}
                          options={flightEventTypes}
                          closeMenuOnSelect={false}
                          hideSelectedOptions={false}
                          isSearchable={true}
                          isClearable={false}
                          menuPosition="fixed"
                          placeholder="example: Training, Cargo, Medivac, etc."
                        />

                        <FormikCheckboxField
                          className="field checkboxfield"
                          label="Match Flight Number"
                          name="matchFlightNum"
                        />
                        <FormikCheckboxField
                          className="field checkboxfield"
                          label="Assign to aircraft"
                          name="assignToAircraft"
                        />
                        <FormikCheckboxField
                          className="field checkboxfield"
                          label="Update Past Flights"
                          name="updatePastFlights"
                        />
                      </Grid>

                      <Grid container className="form_footer">
                        <Button
                          className="btn reset"
                          type="reset"
                          variant="contained"
                          color="secondary"
                          disabled={isSubmitting || !dirty}
                          onClick={() => handleResetImport()}
                        >
                          Reset
                        </Button>

                        <Button
                          className={"btn save"}
                          variant="contained"
                          color="primary"
                          disabled={isSubmitting || !dirty || !!values?.currentAuditId || !isReadyToAudit}
                          type="submit"
                        >
                          Upload and Audit
                        </Button>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                <Accordion expanded={!displayImport} className="audit_accordion">
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ opacity: !displayImport ? 1 : 0.5 }}>
                    <Grid item className="audit_header">
                      <Typography variant="h6" className="title">
                        {auditAccordionTitle}
                      </Typography>
                      <Typography variant="body2">{auditSubTitle}</Typography>
                    </Grid>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Grid container className="audit_table">
                      <AuditEventsCheck
                        fileName={values.fileInfo?.path}
                        cancelAudit={handleResetImport}
                        auditId={values?.currentAuditId}
                        windowId={id}
                        path={path}
                        loadingAudit={loadingAudit}
                        setLoadingAudit={setLoadingAudit}
                      />
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Form>
            );
          }}
        </Formik>
      </Grid>
    </Grid>
  );
};
